
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Users, Target, Shield, AlertTriangle } from "lucide-react";

interface JobScoringPanelProps {
  jobData: any;
}

export function JobScoringPanel({ jobData }: JobScoringPanelProps) {
  // Mock scoring logic - in real implementation, this would be calculated by AI
  const scores = {
    breakthroughLikelihood: 78,
    applicationRate: 65,
    clarityScore: 92,
    inclusivityScore: 88,
    marketCompetitiveness: 72,
  };

  const insights = [
    {
      type: "positive",
      message: "Strong job description with clear responsibilities",
      icon: <Target className="h-4 w-4" />
    },
    {
      type: "warning", 
      message: "Consider adding remote work options to increase applications",
      icon: <AlertTriangle className="h-4 w-4" />
    },
    {
      type: "positive",
      message: "Inclusive language detected throughout posting",
      icon: <Shield className="h-4 w-4" />
    }
  ];

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBadgeVariant = (score: number) => {
    if (score >= 80) return "default";
    if (score >= 60) return "secondary";
    return "destructive";
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Job Performance Prediction
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium">Breakthrough Likelihood</span>
                <Badge variant={getScoreBadgeVariant(scores.breakthroughLikelihood)}>
                  {scores.breakthroughLikelihood}%
                </Badge>
              </div>
              <Progress value={scores.breakthroughLikelihood} className="h-2" />
              <p className="text-xs text-muted-foreground mt-1">
                How likely top candidates are to notice and apply
              </p>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium">Expected Application Rate</span>
                <Badge variant={getScoreBadgeVariant(scores.applicationRate)}>
                  {scores.applicationRate}%
                </Badge>
              </div>
              <Progress value={scores.applicationRate} className="h-2" />
              <p className="text-xs text-muted-foreground mt-1">
                Predicted application rate vs industry average
              </p>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium">Clarity Score</span>
                <Badge variant={getScoreBadgeVariant(scores.clarityScore)}>
                  {scores.clarityScore}%
                </Badge>
              </div>
              <Progress value={scores.clarityScore} className="h-2" />
              <p className="text-xs text-muted-foreground mt-1">
                How clear and well-structured the posting is
              </p>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium">Inclusivity Score</span>
                <Badge variant={getScoreBadgeVariant(scores.inclusivityScore)}>
                  {scores.inclusivityScore}%
                </Badge>
              </div>
              <Progress value={scores.inclusivityScore} className="h-2" />
              <p className="text-xs text-muted-foreground mt-1">
                Use of inclusive language and practices
              </p>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium">Market Competitiveness</span>
                <Badge variant={getScoreBadgeVariant(scores.marketCompetitiveness)}>
                  {scores.marketCompetitiveness}%
                </Badge>
              </div>
              <Progress value={scores.marketCompetitiveness} className="h-2" />
              <p className="text-xs text-muted-foreground mt-1">
                How competitive this role is in current market
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            AI Insights & Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {insights.map((insight, index) => (
              <div key={index} className="flex gap-2">
                <div className={`${insight.type === 'positive' ? 'text-green-600' : 'text-yellow-600'}`}>
                  {insight.icon}
                </div>
                <p className="text-sm">{insight.message}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Quick Stats</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Est. Time to Fill</p>
              <p className="font-medium">18-25 days</p>
            </div>
            <div>
              <p className="text-muted-foreground">Expected Applications</p>
              <p className="font-medium">45-60</p>
            </div>
            <div>
              <p className="text-muted-foreground">Quality Score</p>
              <p className="font-medium">8.2/10</p>
            </div>
            <div>
              <p className="text-muted-foreground">Competition Level</p>
              <p className="font-medium">High</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
